import { useQuery } from '@tanstack/react-query';

// Cache name for document files
const CACHE_NAME = 'document-cache-v1';

/**
 * Downloads and caches document file from a presigned URL.
 * Returns a blob URL for rendering images/PDFs.
 * 
 * Technical Implementation:
 * - Two-layer caching: Browser Cache API (persistent, disk-based) + TanStack Query (in-memory)
 * - Cache API stores raw Response objects, survives page refreshes and browser restarts
 * - TanStack Query caches blob URLs (URL.createObjectURL) to avoid re-parsing on re-renders
 * - Flow: Component → Query cache (memory) → Cache API (disk) → fetch(S3) → blob URL
 * - First load: Network fetch + Cache API store + Query cache
 * - Same session: Query cache hit (instant)
 * - After refresh: Cache API hit (fast, no network)
 * - After 24h idle: Re-fetch from S3
 * 
 * @param url - Presigned S3 URL from usePresignedUrl hook
 * @param fileKey - Document file key for cache identification
 * @returns Blob URL for rendering in <img> or <iframe>
 */
export function useCachedDocumentFile(url: string | null, fileKey: string) {
  return useQuery({
    queryKey: ['cachedDocumentFile', fileKey],
    queryFn: async () => {
      if (!url) throw new Error('No URL provided');
      
      try {
        // Try to get from cache first
        const cache = await caches.open(CACHE_NAME);
        let response = await cache.match(url);
        
        // If not in cache or cache is disabled, fetch it
        if (!response) {
          console.log(`[Cache] MISS for ${fileKey} - fetching from network`);
          response = await fetch(url);
          
          // Clone the response before putting in cache
          const responseToCache = response.clone();
          await cache.put(url, responseToCache);
          console.log(`[Cache] Stored ${fileKey} in browser cache`);
        } else {
          console.log(`[Cache] HIT for ${fileKey} - serving from browser cache`);
        }
        
        // For images, return as blob URL
        const blob = await response.blob();
        return URL.createObjectURL(blob);
      } catch (error) {
        console.error('Error fetching document file:', error);
        throw error;
      }
    },
    enabled: !!url,
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
  });
}